/* Copyright (c) Trimble Inc. All rights reserved. */
import { useEffect, useRef, useState } from 'react';

/**
 * Polling Configuration
 */
export interface UsePollingConfig {
  /** Callback function executed on each poll attempt */
  onPoll: () => void;
  /** Function that returns true when polling should stop */
  exitCondition: () => boolean;
  /** Polling interval in milliseconds */
  intervalMs: number;
  /** Maximum number of polling attempts */
  maxAttempts: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Polling Hook Result
 */
export interface UsePollingResult {
  /** Whether the maximum polling attempts have been reached */
  isTimedOut: boolean;
  /** Restart the polling loop from the beginning */
  restart: () => void;
  /** Current attempt number (1-indexed) */
  attempts: number;
}

/**
 * usePolling Hook
 *
 * Manages a polling loop with configurable interval, max attempts, and exit conditions.
 * Executes onPoll callback on each attempt until either:
 * 1. Exit condition is met (success)
 * 2. Max attempts reached (timeout)
 *
 * @example
 * ```typescript
 * const { isTimedOut, restart, attempts } = usePolling({
 *   onPoll: () => {
 *     // Perform polling action (e.g., check account status)
 *   },
 *   exitCondition: () => accountsExist, // Exit when true
 *   intervalMs: 3000,                   // Poll every 3 seconds
 *   maxAttempts: 10,                    // Max 30 seconds total
 *   enabled: true,                      // Can be toggled on/off
 * });
 *
 * if (isTimedOut) {
 *   return <TimeoutMessage onRetry={restart} />;
 * }
 * ```
 */
export const usePolling = ({
  onPoll,
  exitCondition,
  intervalMs,
  maxAttempts,
  enabled = true,
}: UsePollingConfig): UsePollingResult => {
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [restartCounter, setRestartCounter] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef(0);

  // Restart polling
  const restart = () => {
    setIsTimedOut(false);
    setAttempts(0);
    attemptsRef.current = 0;

    // Clear existing interval if any
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Increment restart counter to trigger effect re-run
    setRestartCounter((prev) => prev + 1);
  };

  // Main polling effect
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let shouldContinuePolling = true;

    // Execute the first poll immediately
    const executePolling = () => {
      // Check if we've exceeded max attempts
      if (attemptsRef.current >= maxAttempts) {
        setIsTimedOut(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        shouldContinuePolling = false;
        return;
      }

      // Increment attempt counter
      attemptsRef.current += 1;
      setAttempts(attemptsRef.current);

      // Execute the poll callback
      onPoll();

      // Check exit condition
      const exitConditionMet = exitCondition();
      if (exitConditionMet) {
        // Exit condition met - stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        shouldContinuePolling = false;
        return;
      }

      // If max attempts reached, timeout
      if (attemptsRef.current >= maxAttempts) {
        setIsTimedOut(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        shouldContinuePolling = false;
      }
    };

    // Execute first poll immediately
    executePolling();

    // Set up interval for subsequent polls only if we should continue
    if (shouldContinuePolling && attemptsRef.current < maxAttempts) {
      intervalRef.current = setInterval(executePolling, intervalMs);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, onPoll, exitCondition, intervalMs, maxAttempts, restartCounter]);

  return {
    isTimedOut,
    restart,
    attempts,
  };
};

export default usePolling;
